import { DragEvent, FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Building2,
  GripVertical,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Outlet, useOutlet } from "@/contexts/OutletContext";
import { useSubscription } from "@/hooks/useSubscription";
import { checkOutletLimit } from "@/lib/limits";
import { plans } from "@/config/stripePlans";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import {
  StaffInviteDialog,
  type StaffInviteSubmit,
} from "@/components/admin_dashboard/StaffAccessDialogs";

type BranchDialogState =
  | { mode: "create"; branch: null }
  | { mode: "edit"; branch: Outlet }
  | null;

type StaffRow = {
  id: string;
  email: string;
  display_name: string | null;
  outlet_id: string;
  status: string;
};

type StaffMember = {
  email: string;
  displayName: string | null;
  status: "active" | "pending";
  outletIds: string[];
};

const LIVE_STAFF_STATUSES = ["pending", "active"];
const STAFF_DRAG_TYPE = "application/x-dibs-staff-email";
const DEV_TEST_STAFF_EMAIL = "staff.test@dibs.invalid";

const developmentStaffRow = (outletId: string): StaffRow => ({
  id: `dev-test-staff-${outletId}`,
  email: DEV_TEST_STAFF_EMAIL,
  display_name: "Test Staff",
  outlet_id: outletId,
  status: "active",
});

const branchInitial = (name: string) => (name.trim().charAt(0) || "B").toUpperCase();
const staffInitials = (member: StaffMember) => {
  const label = member.displayName?.trim() || member.email;
  return label
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
};

export default function BranchSettings() {
  const { businessId } = useAuth();
  const { outlets, refreshOutlets } = useOutlet();
  const { subscriptionTier } = useSubscription();
  const [branchDialog, setBranchDialog] = useState<BranchDialogState>(null);
  const [branchName, setBranchName] = useState("");
  const [branchAddress, setBranchAddress] = useState("");
  const [branchToRemove, setBranchToRemove] = useState<Outlet | null>(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [staffRows, setStaffRows] = useState<StaffRow[]>([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [draggedStaffEmail, setDraggedStaffEmail] = useState<string | null>(null);
  const [dragOverBranchId, setDragOverBranchId] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [unassigning, setUnassigning] = useState<string | null>(null);

  const branches = useMemo(
    () => outlets.filter((outlet) => outlet.is_active !== false),
    [outlets],
  );
  const currentPlan = plans[(subscriptionTier || "free").toLowerCase()] || plans.free;
  const staffAllowance = currentPlan.limits.staff ?? "unlimited";
  const canAddBranch = checkOutletLimit(subscriptionTier, branches.length);
  const staffMembers = useMemo<StaffMember[]>(() => {
    const byEmail = new Map<string, StaffRow[]>();
    for (const row of staffRows) {
      if (!LIVE_STAFF_STATUSES.includes(row.status)) continue;
      const email = row.email.toLowerCase();
      byEmail.set(email, [...(byEmail.get(email) || []), row]);
    }

    return Array.from(byEmail.entries())
      .map(([email, rows]) => ({
        email,
        displayName: rows.find((row) => row.display_name)?.display_name ?? null,
        status: rows.some((row) => row.status === "active") ? "active" as const : "pending" as const,
        outletIds: Array.from(new Set(rows.map((row) => row.outlet_id))),
      }))
      .sort((a, b) =>
        (a.displayName || a.email).localeCompare(b.displayName || b.email),
      );
  }, [staffRows]);
  const branchFormChanged = branchDialog?.mode === "create"
    ? Boolean(branchName.trim())
    : branchDialog?.mode === "edit"
      ? branchName.trim() !== branchDialog.branch.outlet_name ||
        branchAddress.trim() !== (branchDialog.branch.address || "").trim()
      : false;

  const loadStaff = useCallback(async () => {
    if (!businessId) {
      setStaffRows([]);
      setStaffLoading(false);
      return;
    }

    setStaffLoading(true);
    const { data, error } = await supabase
      .from("staff_members")
      .select("id, email, display_name, outlet_id, status")
      .eq("business_id", businessId)
      .in("status", LIVE_STAFF_STATUSES)
      .order("invited_at", { ascending: true });

    if (error) {
      toast({
        title: "Could not load staff",
        description: "Refresh the page and try again.",
        variant: "destructive",
      });
    } else {
      const loadedRows = (data || []) as StaffRow[];
      setStaffRows(
        import.meta.env.DEV && loadedRows.length === 0 && branches[0]
          ? [developmentStaffRow(branches[0].outlet_id)]
          : loadedRows,
      );
    }
    setStaffLoading(false);
  }, [branches, businessId]);

  useEffect(() => {
    void loadStaff();
  }, [loadStaff]);

  const inviteStaff = async ({ email, displayName, outletIds }: StaffInviteSubmit) => {
    if (!businessId) return;

    setInviting(true);
    try {
      const { data, error } = await supabase.functions.invoke("invite-staff", {
        body: {
          email,
          displayName,
          businessId,
          outletIds,
          origin: window.location.origin,
        },
      });

      if (error || data?.success === false) {
        toast({
          title: "Could not send invitation",
          description: data?.error || "Please try again.",
          variant: "destructive",
        });
        return;
      }

      setInviteOpen(false);
      await loadStaff();
      toast({
        title: "Invitation sent",
        description: data?.message || `An invitation has been sent to ${email}.`,
      });
    } catch (error: any) {
      toast({
        title: "Could not send invitation",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setInviting(false);
    }
  };

  const assignStaffToBranch = async (member: StaffMember, branch: Outlet) => {
    if (!businessId) return;
    if (member.outletIds.includes(branch.outlet_id)) {
      toast({
        title: "Already assigned",
        description: `${member.displayName || member.email} already has access to ${branch.outlet_name}.`,
      });
      return;
    }

    const assignmentKey = `${member.email}:${branch.outlet_id}`;
    setAssigning(assignmentKey);

    if (import.meta.env.DEV && member.email === DEV_TEST_STAFF_EMAIL) {
      setStaffRows((current) => [...current, developmentStaffRow(branch.outlet_id)]);
      toast({
        title: "Test staff assigned",
        description: `${member.displayName || member.email} can now access ${branch.outlet_name} in this development session.`,
      });
      setAssigning(null);
      setDraggedStaffEmail(null);
      setDragOverBranchId(null);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("invite-staff", {
        body: {
          email: member.email,
          displayName: member.displayName || "",
          businessId,
          outletIds: [branch.outlet_id],
          origin: window.location.origin,
        },
      });

      if (error || data?.success === false) {
        toast({
          title: "Could not assign staff",
          description: data?.error || "Please try again.",
          variant: data?.code === "ALREADY_HAS_ACCESS" ? "default" : "destructive",
        });
        if (data?.code === "ALREADY_HAS_ACCESS") await loadStaff();
        return;
      }

      await loadStaff();
      toast({
        title: "Staff assigned",
        description: `${member.displayName || member.email} can now access ${branch.outlet_name}.`,
      });
    } catch (error: any) {
      toast({
        title: "Could not assign staff",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setAssigning(null);
      setDraggedStaffEmail(null);
      setDragOverBranchId(null);
    }
  };

  const unassignStaffFromBranch = async (member: StaffMember, branch: Outlet) => {
    if (!businessId) return;

    const assignmentKey = `${member.email}:${branch.outlet_id}`;
    setUnassigning(assignmentKey);

    if (import.meta.env.DEV && member.email === DEV_TEST_STAFF_EMAIL) {
      setStaffRows((current) =>
        current.filter(
          (row) =>
            row.email.toLowerCase() !== member.email ||
            row.outlet_id !== branch.outlet_id,
        ),
      );
      toast({
        title: "Test staff unassigned",
        description: `${member.displayName || member.email} no longer has access to ${branch.outlet_name} in this development session.`,
      });
      setUnassigning(null);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("manage-staff-access", {
        body: {
          action: "remove-outlets",
          email: member.email,
          businessId,
          outletIds: [branch.outlet_id],
          origin: window.location.origin,
        },
      });

      if (error || data?.success === false) {
        toast({
          title: "Could not unassign staff",
          description: data?.error || "Please try again.",
          variant: "destructive",
        });
        return;
      }

      await loadStaff();
      toast({
        title: "Staff unassigned",
        description: `${member.displayName || member.email} no longer has access to ${branch.outlet_name}.`,
      });
    } catch (error: any) {
      toast({
        title: "Could not unassign staff",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setUnassigning(null);
    }
  };

  const handleStaffDragStart = (event: DragEvent<HTMLDivElement>, member: StaffMember) => {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData(STAFF_DRAG_TYPE, member.email);
    event.dataTransfer.setData("text/plain", member.email);
    setDraggedStaffEmail(member.email);
  };

  const handleBranchDrop = (event: DragEvent<HTMLDivElement>, branch: Outlet) => {
    event.preventDefault();
    const email = event.dataTransfer.getData(STAFF_DRAG_TYPE) || event.dataTransfer.getData("text/plain");
    const member = staffMembers.find((candidate) => candidate.email === email.toLowerCase());
    setDragOverBranchId(null);
    if (member) void assignStaffToBranch(member, branch);
  };

  const openCreateDialog = () => {
    if (!canAddBranch) return;
    setBranchName("");
    setBranchAddress("");
    setBranchDialog({ mode: "create", branch: null });
  };

  const openEditDialog = (branch: Outlet) => {
    setBranchName(branch.outlet_name);
    setBranchAddress(branch.address || "");
    setBranchDialog({ mode: "edit", branch });
  };

  const handleSaveBranch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = branchName.trim();
    const address = branchAddress.trim();
    if (!name || !businessId || !branchDialog) return;

    const duplicate = branches.some(
      (branch) =>
        branch.outlet_id !== branchDialog.branch?.outlet_id &&
        branch.outlet_name.trim().toLocaleLowerCase() === name.toLocaleLowerCase(),
    );
    if (duplicate) {
      toast({
        title: "Branch name already in use",
        description: "Choose a different name for this branch.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      if (branchDialog.mode === "edit") {
        const { error } = await supabase
          .from("outlets")
          .update({ outlet_name: name, address: address || null })
          .eq("outlet_id", branchDialog.branch.outlet_id)
          .eq("business_id", businessId);
        if (error) throw error;
        toast({ title: "Branch updated", description: `${name}'s details have been saved.` });
      } else {
        if (!canAddBranch) throw new Error("Your current plan has reached its branch limit.");

        const { data: business, error: businessError } = await supabase
          .from("businesses")
          .select("description, phone_number, business_email, instagram_url, facebook_url, twitter_url, avatar_url")
          .eq("business_id", businessId)
          .single();
        if (businessError) throw businessError;

        const slugBase = name
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "-")
          .replace(/-+/g, "-")
          .replace(/^-|-$/g, "") || "branch";
        const slug = `${slugBase}-${Math.random().toString(36).slice(2, 8)}`;
        const { error } = await supabase.from("outlets").insert({
          business_id: businessId,
          outlet_name: name,
          address: address || null,
          description: business?.description || null,
          phone_number: business?.phone_number || null,
          business_email: business?.business_email || null,
          instagram_url: business?.instagram_url || null,
          facebook_url: business?.facebook_url || null,
          twitter_url: business?.twitter_url || null,
          avatar_url: business?.avatar_url || null,
          slug,
          is_active: true,
        });
        if (error) throw error;
        toast({ title: "Branch added", description: `${name} is ready to set up.` });
      }

      await refreshOutlets();
      setBranchDialog(null);
      setBranchName("");
      setBranchAddress("");
    } catch (error: any) {
      toast({
        title: branchDialog.mode === "create" ? "Could not add branch" : "Could not update branch",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveBranch = async () => {
    if (!branchToRemove || !businessId || branches.length <= 1) return;
    setRemoving(true);
    try {
      const { error } = await supabase
        .from("outlets")
        .update({ is_active: false })
        .eq("outlet_id", branchToRemove.outlet_id)
        .eq("business_id", businessId);
      if (error) throw error;

      const removedName = branchToRemove.outlet_name;
      await refreshOutlets();
      setBranchToRemove(null);
      toast({ title: "Branch deleted", description: `${removedName} has been deleted.` });
    } catch (error: any) {
      toast({
        title: "Could not delete branch",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRemoving(false);
    }
  };

  return (
    <main className="space-y-8">
      <header className="min-w-0 space-y-1">
        <h1 className="text-xl font-heading font-semibold text-foreground">Branches and staff</h1>
        <p className="text-sm text-muted-foreground">
          Manage your branches and decide where each staff member can work.
        </p>
      </header>

      <section aria-labelledby="branches-heading">
        <div className="flex items-center gap-4">
          <h2 id="branches-heading" className="shrink-0 font-heading text-base font-semibold text-foreground">
            Branches
          </h2>
          <div className="h-px flex-1 bg-border" aria-hidden="true" />
        </div>

        <div className="mt-4 grid items-start gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {branches.map((branch) => {
            const isOnlyBranch = branches.length <= 1;
            const assignedStaff = staffMembers.filter((member) =>
              member.outletIds.includes(branch.outlet_id),
            );
            const isDropTarget = dragOverBranchId === branch.outlet_id;

            return (
              <Card
                key={branch.outlet_id}
                className={cn(
                  "flex flex-col shadow-none transition-colors",
                  isDropTarget && "border-primary bg-primary/[0.03] ring-2 ring-primary/20",
                )}
                onDragOver={(event) => {
                  if (!draggedStaffEmail || assigning) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "copy";
                  setDragOverBranchId(branch.outlet_id);
                }}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                    setDragOverBranchId(null);
                  }
                }}
                onDrop={(event) => handleBranchDrop(event, branch)}
              >
                <CardHeader className="flex-row items-start justify-between space-y-0 pb-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
                      {branchInitial(branch.outlet_name)}
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="truncate text-base leading-6">{branch.outlet_name}</CardTitle>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {branch.address || "No address provided"}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label={`Actions for ${branch.outlet_name}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(branch)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                          disabled={isOnlyBranch}
                          onClick={() => setBranchToRemove(branch)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="px-6 pb-5 pt-0">
                  <div className="pt-1">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-muted-foreground">
                        Staff ({assignedStaff.length})
                      </p>
                      {isDropTarget && (
                        <span className="text-xs font-medium text-primary">Drop to assign</span>
                      )}
                    </div>
                    {assignedStaff.length > 0 ? (
                      <div className="space-y-2">
                        {assignedStaff.map((member) => {
                          const assignmentKey = `${member.email}:${branch.outlet_id}`;
                          const isUnassigning = unassigning === assignmentKey;
                          return (
                            <div
                              key={member.email}
                              className="group flex min-h-10 w-full min-w-0 items-center gap-2 rounded-md bg-secondary px-3 py-2"
                              title={`${member.displayName || member.email} · ${member.email}`}
                            >
                              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-background text-[10px] font-semibold text-foreground">
                                {staffInitials(member)}
                              </span>
                              <span className="truncate text-xs font-medium text-foreground">
                                {member.displayName || member.email}
                              </span>
                              {member.status === "pending" && (
                                <span
                                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                                  title="Invitation pending"
                                  aria-label="Invitation pending"
                                />
                              )}
                              {assigning === assignmentKey && !isUnassigning && (
                                <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
                              )}
                              <button
                                type="button"
                                className="pointer-events-none ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground opacity-0 transition-colors hover:bg-background hover:text-foreground focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:pointer-events-auto group-hover:opacity-100 disabled:pointer-events-none disabled:opacity-100"
                                disabled={isUnassigning}
                                aria-label={`Unassign ${member.displayName || member.email} from ${branch.outlet_name}`}
                                title={`Unassign from ${branch.outlet_name}`}
                                onClick={() => void unassignStaffFromBranch(member, branch)}
                              >
                                {isUnassigning ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <X className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div
                        className={cn(
                          "flex min-h-10 items-center justify-center rounded-md border border-dashed px-3 text-center text-xs text-muted-foreground",
                          isDropTarget && "border-primary/50 bg-primary/5 text-primary",
                        )}
                      >
                        {isDropTarget ? "Drop to grant branch access" : "Drop staff here"}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          <button
            type="button"
            onClick={openCreateDialog}
            disabled={!canAddBranch}
            className="flex min-h-40 flex-col items-center justify-center rounded-lg border border-dashed bg-muted/20 px-6 py-8 text-center transition-colors hover:border-primary/50 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border disabled:hover:bg-muted/20"
          >
            <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              {canAddBranch ? <Plus className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
            </span>
            <span className="text-sm font-semibold text-foreground">
              {canAddBranch ? "Add a new branch" : "Branch limit reached"}
            </span>
            <span className="mt-1 max-w-52 text-xs text-muted-foreground">
              {canAddBranch
                ? "Create another workspace for a location or team."
                : "Upgrade your plan to add another branch."}
            </span>
          </button>
        </div>
      </section>

      <section aria-labelledby="staff-heading">
        <div className="flex items-center gap-4">
          <h2 id="staff-heading" className="shrink-0 font-heading text-base font-semibold text-foreground">
            Staff
          </h2>
          <div className="h-px flex-1 bg-border" aria-hidden="true" />
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-1 text-sm text-muted-foreground">
          <p>Drag a staff member onto a branch to give them access.</p>
          <p>
            {staffMembers.length} out of {staffAllowance} allowed on {currentPlan.name} plan
          </p>
        </div>

        {staffLoading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading staff…
          </div>
        ) : (
          <div className="mt-4 grid items-start gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {staffMembers.map((member) => {
              const isDragging = draggedStaffEmail === member.email;
              const isAssigningMember = assigning?.startsWith(`${member.email}:`) ?? false;

              return (
                <div
                  key={member.email}
                  draggable={!isAssigningMember}
                  onDragStart={(event) => handleStaffDragStart(event, member)}
                  onDragEnd={() => {
                    setDraggedStaffEmail(null);
                    setDragOverBranchId(null);
                  }}
                  className={cn(
                    "flex h-[62px] min-w-0 cursor-grab items-center gap-3 rounded-lg border bg-card p-3 shadow-none transition-opacity active:cursor-grabbing",
                    isDragging && "opacity-50",
                    isAssigningMember && "cursor-wait opacity-70",
                  )}
                >
                  <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-foreground">
                    {staffInitials(member)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <p className="truncate text-sm font-medium text-foreground">
                        {member.displayName || member.email}
                      </p>
                      {member.status === "pending" && (
                        <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-[10px] capitalize">
                          Invited
                        </Badge>
                      )}
                    </div>
                    {member.displayName && (
                      <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                    )}
                  </div>
                  {isAssigningMember && (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="flex h-[62px] items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 px-6 py-3 text-center transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <UserPlus className="h-4 w-4" />
              </span>
              <span className="text-sm font-semibold text-foreground">Invite staff</span>
            </button>
          </div>
        )}
      </section>

      <StaffInviteDialog
        open={inviteOpen}
        onOpenChange={(open) => {
          if (!inviting) setInviteOpen(open);
        }}
        outlets={branches}
        submitting={inviting}
        onSubmit={(values) => void inviteStaff(values)}
      />

      <Dialog
        open={branchDialog !== null}
        onOpenChange={(open) => {
          if (!open && !saving) setBranchDialog(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleSaveBranch}>
            <DialogHeader className="admin-modal-header">
              <DialogTitle>{branchDialog?.mode === "create" ? "Add a new branch" : "Edit branch"}</DialogTitle>
              <DialogDescription>
                {branchDialog?.mode === "create"
                  ? "Add the branch name and address your team will recognize."
                  : "Update the branch name and address shown across Dibs."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-5">
              <div>
                <Label htmlFor="branch-name">Branch name</Label>
                <Input
                  id="branch-name"
                  value={branchName}
                  onChange={(event) => setBranchName(event.target.value)}
                  placeholder="e.g. Tampines"
                  maxLength={80}
                  autoFocus
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="branch-address">Address</Label>
                <Textarea
                  id="branch-address"
                  value={branchAddress}
                  onChange={(event) => setBranchAddress(event.target.value)}
                  placeholder="e.g. 10 Tampines Central 1"
                  rows={3}
                  className="mt-2 min-h-24 resize-y"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBranchDialog(null)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !branchName.trim() || !branchFormChanged}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {branchDialog?.mode === "create" ? "Add branch" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={branchToRemove !== null}
        onOpenChange={(open) => {
          if (!open && !removing) setBranchToRemove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {branchToRemove?.outlet_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This branch will disappear from the branch switcher and stop accepting new bookings. Existing business
              records will be kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={removing}
              onClick={(event) => {
                event.preventDefault();
                void handleRemoveBranch();
              }}
            >
              {removing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete branch
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
